/**
 * The payment-gateway row: is Stripe configured, how much of the catalogue has
 * reached it, and the button that pushes the rest.
 */
import { CreditCard, RefreshCw } from "lucide-react";
import { cn, type BillingProviderStatus, type PlansT, type SubscriptionsSyncMessage } from "@wa/ui";
import { Btn, Card, StatusPill } from "../../ui";

export function StripeStrip({
  stripe,
  syncing,
  msg,
  onSync,
  t,
}: {
  stripe: BillingProviderStatus | null;
  syncing: boolean;
  /** Result note of the last sync OR the last refund - the hook shares one slot. */
  msg: SubscriptionsSyncMessage | null;
  onSync: () => void;
  t: PlansT;
}) {
  // Nothing to say: neither a gateway to describe nor a result to report.
  if (!stripe && !msg) return null;

  return (
    <Card className="mb-4 px-6 py-4">
      {stripe ? (
        <div className="flex flex-wrap items-center gap-3">
          <CreditCard className="size-4 text-[var(--color-primary)]" />
          <span className="text-[13.25px] font-semibold">{t("subs.stripe.title")}</span>
          <StatusPill tone={stripe.configured ? "ok" : "mut"}>
            {stripe.configured ? t("subs.stripe.configured") : t("subs.stripe.notConfigured")}
          </StatusPill>
          {stripe.configured ? (
            <span className="crm-mut crm-tnum text-[12.25px]">
              {stripe.plansSynced}/{stripe.payablePlans} {t("subs.stripe.syncedSuffix")}
            </span>
          ) : null}
          <span className="crm-push" />
          <Btn small onClick={onSync} disabled={!stripe.configured || syncing}>
            <RefreshCw className={cn("size-3.5", syncing && "animate-spin")} />
            {syncing ? t("subs.stripe.syncing") : t("subs.stripe.sync")}
          </Btn>
        </div>
      ) : null}

      {/* Outside the gateway block on purpose. A refund writes its outcome into
          the same slot as a sync, and in the shared table that note lived inside
          the `stripe &&` branch - so whenever `GET /billing/status` failed, a
          refund reported neither success nor failure to anybody. */}
      {msg ? (
        <p
          className={cn(
            "text-[12.25px]",
            stripe && "mt-3",
            msg.ok ? "text-success" : "text-destructive",
          )}
        >
          {msg.text}
        </p>
      ) : null}
    </Card>
  );
}
